/**
 * MappingHealthSummary Component
 *
 * Compact mapping-health representation for the IRP Workbook slim top bar.
 * Shows the full status breakdown (resolved / partial / excluded / errors) on the
 * Overview tab, and just the report count elsewhere.
 */

import React from 'react';
import {Box, Chip, Tooltip, Typography} from '@mui/material';
import {IrpWorkbookOverviewDTO} from '../../../../../../@types/integrated-risk-picture';

interface MappingHealthSummaryProps {
    overview: IrpWorkbookOverviewDTO | null;
    /** When true, show only the total report count (used off the Overview tab). */
    compact?: boolean;
}

const MappingHealthSummary: React.FC<MappingHealthSummaryProps> = ({ overview, compact = false }) => {
    if (!overview) {
        return null;
    }

    const { totalReportCount, mappedReportCount, skippedReportCount, errorReportCount, integrityIssueCount } = overview;
    const partialCount = integrityIssueCount ?? 0;

    const reportCount = (
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {totalReportCount} reports
        </Typography>
    );

    if (compact) {
        return reportCount;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {reportCount}
            <Tooltip title="Fully resolved — the LLM traversed the full fault tree and resolved every branch point.">
                <Chip size="small" variant="outlined" color="success" label={`${mappedReportCount} resolved`} sx={{ height: 22 }} />
            </Tooltip>
            {partialCount > 0 && (
                <Tooltip title="Partial — the LLM could not resolve a branch point; the mapping may need human review.">
                    <Chip size="small" variant="outlined" color="info" label={`${partialCount} partial`} sx={{ height: 22 }} />
                </Tooltip>
            )}
            <Tooltip title="Excluded — evaluated but not applicable to this safety model.">
                <Chip size="small" variant="outlined" color="warning" label={`${skippedReportCount} excluded`} sx={{ height: 22 }} />
            </Tooltip>
            {errorReportCount > 0 && (
                <Tooltip title="Errors — could not be processed due to a technical error; may succeed if re-run.">
                    <Chip size="small" variant="outlined" color="error" label={`${errorReportCount} errors`} sx={{ height: 22 }} />
                </Tooltip>
            )}
        </Box>
    );
};

export default MappingHealthSummary;
